import React, { useCallback } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import { withTheme } from "@material-ui/styles";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import ProgressInputLabel from "./ProgressInputLabel";

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const Control = withTheme(styled(FormControl)`
  flex-grow: 1;
  && {
    margin: 6px 0;
    min-width: 150px;
    width: 100%;
  }

  ${(props) =>
    props.disabled &&
    css`
      opacity: 50%;
    `}/* .MuiMenu-paper {
    max-width: 150px;
    width: 150px !important;
  } */
`);

const StyledSelect = styled(Select)`
  margin-top: 13px;
  .MuiSelect-root {
    padding: 20px;
  }
`;

const HierarchySelect = ({ values, onChange, value, ...props }) => (
  <StyledSelect value={value} onChange={onChange} {...props} variant="outlined">
    {values &&
      values.map(({ name, value }) => (
        <MenuItem key={value} value={value}>
          {name.toUpperCase()}
        </MenuItem>
      ))}
  </StyledSelect>
);

const sortFranchisesFirst = (franchises = []) => {
  const normalizedFranchises = franchises.map((name) => name.toUpperCase());

  return ({ name: a }, { name: b }) => {
    const aIdx = normalizedFranchises.indexOf(a.toUpperCase());
    const bIdx = normalizedFranchises.indexOf(b.toUpperCase());
    return bIdx - aIdx;
  };
};

const YMMTSelect = ({
  name,
  yearId,
  makeId,
  modelId,
  trimId,
  years,
  makes,
  models,
  trims,
  franchises,
  onChange,
  ...props
}) => {
  const handleYearChange = useCallback(
    (evt) => {
      const { value } = evt.target;
      onChange({
        target: {
          name,
          value: { yearId: value, makeId: "", modelId: "", trimId: "" },
        },
      });
    },
    [name, onChange]
  );

  const handleMakeChange = useCallback(
    (evt) => {
      const { value } = evt.target;
      onChange({
        target: {
          name,
          value: { yearId, makeId: value, modelId: "", trimId: "" },
        },
      });
    },
    [name, onChange, yearId]
  );

  const handleModelChange = useCallback(
    (evt) => {
      const { value } = evt.target;
      onChange({
        target: {
          name,
          value: { yearId, makeId, modelId: value, trimId: "" },
        },
      });
    },
    [name, onChange, yearId, makeId]
  );

  const handleTrimChange = useCallback(
    (evt) => {
      const { value } = evt.target;
      onChange({
        target: {
          name,
          value: { yearId, makeId, modelId, trimId: value },
        },
      });
    },
    [name, onChange, yearId, makeId, modelId]
  );

  return (
    <Wrapper {...props}>
      <Control disabled={!years}>
        <ProgressInputLabel htmlFor="year-select" loading={!years}>
          YEAR
        </ProgressInputLabel>
        <HierarchySelect
          id="year-select"
          name="year"
          value={yearId}
          values={years}
          disabled={!years}
          onChange={handleYearChange}
        />
      </Control>
      <Control disabled={!yearId}>
        <ProgressInputLabel htmlFor="make-select" loading={yearId && !makes}>
          MAKE
        </ProgressInputLabel>
        <HierarchySelect
          id="make-select"
          name="make"
          value={makeId}
          values={makes && makes.sort(sortFranchisesFirst(franchises))}
          disabled={!yearId}
          onChange={handleMakeChange}
        />
      </Control>
      <Control disabled={!makeId}>
        <ProgressInputLabel htmlFor="model-select" loading={makeId && !models}>
          MODEL
        </ProgressInputLabel>
        <HierarchySelect
          id="model-select"
          name="model"
          value={modelId}
          values={models}
          disabled={!makeId}
          onChange={handleModelChange}
        />
      </Control>
      <Control disabled={!modelId}>
        <ProgressInputLabel htmlFor="trim-select" loading={modelId && !trims}>
          TRIM
        </ProgressInputLabel>
        <HierarchySelect
          id="trim-select"
          name="trim"
          value={trimId}
          values={trims}
          disabled={!modelId}
          onChange={handleTrimChange}
        />
      </Control>
    </Wrapper>
  );
};

const SelectorObjectType = PropTypes.shape({
  name: PropTypes.string,
  value: PropTypes.string,
});

YMMTSelect.propTypes = {
  yearId: PropTypes.string,
  makeId: PropTypes.string,
  modelId: PropTypes.string,
  trimId: PropTypes.string,
  years: PropTypes.arrayOf(SelectorObjectType),
  makes: PropTypes.arrayOf(SelectorObjectType),
  models: PropTypes.arrayOf(SelectorObjectType),
  trims: PropTypes.arrayOf(SelectorObjectType),
};

YMMTSelect.defaultProps = {
  yearId: "",
  makeId: "",
  modelId: "",
  trimId: "",
  franchises: [],
};

export default YMMTSelect;
